import React, { useMemo, useState } from 'react';
import { IconArmchair as Armchair, IconCircle as Circle, IconWalk as Footprints, IconHexagon as Hexagon, IconMinus as Minus, IconLayoutRows as Rows3, IconShape as Shapes, IconSquare as Square, IconTriangle as Triangle, IconTypography as Type, IconToolsKitchen2 as UtensilsCrossed } from '@tabler/icons-react';
import { nextItemLabel, totalCapacity } from './sections';
import { IconPicker, OBJECT_ICONS } from './objectIcons';
import type { GaType, ObjectShape, Plan, TableShape } from './types';
import { Button, Choice, cx, Field, NumberInput, SelectInput, TextInput, Toggle } from './ui';

export type CreateRequest =
    | { kind: 'section'; name: string; rows: number; seats: number }
    | { kind: 'ga'; name: string; capacity: number; gaType: GaType; showIcon: boolean }
    | { kind: 'table'; name: string; shape: TableShape; seats: number; endSeats: number }
    | { kind: 'object'; name: string; shape: ObjectShape; icon?: string; showIcon: boolean; sides: number }
    | { kind: 'text'; text: string; fontSize: number };

type Tab = 'section' | 'table' | 'object' | 'text';

const TABS: { id: Tab; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'section', label: 'Section', Icon: Rows3 },
    { id: 'table', label: 'Table', Icon: UtensilsCrossed },
    { id: 'object', label: 'Object', Icon: Shapes },
    { id: 'text', label: 'Text', Icon: Type },
];

const AddPanel: React.FC<{ plan: Plan; onCreate: (req: CreateRequest) => void }> = ({ plan, onCreate }) => {
    const [tab, setTab] = useState<Tab>('section');
    const autoLabel = useMemo(() => nextItemLabel(plan), [plan]);
    const capacity = useMemo(() => totalCapacity(plan), [plan]);
    const [label, setLabel] = useState<string | null>(null);

    const [sectionType, setSectionType] = useState<'assigned' | 'ga'>('assigned');
    const [rows, setRows] = useState(5);
    const [seats, setSeats] = useState(10);
    const [gaType, setGaType] = useState<GaType>('standing');
    const [gaIcon, setGaIcon] = useState(true);
    const [gaCapacity, setGaCapacity] = useState(50);

    const [tableShape, setTableShape] = useState<TableShape>('round');
    const [tableSeats, setTableSeats] = useState(8);
    const [endSeats, setEndSeats] = useState(2);

    const [objectShape, setObjectShape] = useState<ObjectShape>('square');
    const [objectLabel, setObjectLabel] = useState<string | null>(null);
    const [objectIcon, setObjectIcon] = useState<string>('stage');
    const [showIcon, setShowIcon] = useState(true);
    const [sides, setSides] = useState(6);

    const [text, setText] = useState('Label');
    const [fontSize, setFontSize] = useState(16);

    const name = (label ?? autoLabel).trim() || autoLabel;
    const iconLabel = OBJECT_ICONS.find(i => i.id === objectIcon)?.label ?? 'Object';
    const objectName = objectLabel ?? (showIcon && objectShape !== 'line' ? iconLabel : objectShape === 'line' ? '' : 'Object');

    const create = () => {
        if (tab === 'section') {
            onCreate(sectionType === 'ga'
                ? { kind: 'ga', name, capacity: gaCapacity, gaType, showIcon: gaIcon }
                : { kind: 'section', name, rows, seats });
            setLabel(null);
        } else if (tab === 'table') {
            onCreate({ kind: 'table', name, shape: tableShape, seats: tableSeats, endSeats: tableShape === 'rect' ? Math.min(endSeats, tableSeats) : 0 });
            setLabel(null);
        } else if (tab === 'object') {
            onCreate({ kind: 'object', name: objectName, shape: objectShape, icon: showIcon && objectShape !== 'line' ? objectIcon : undefined, showIcon: showIcon && objectShape !== 'line', sides });
        } else {
            onCreate({ kind: 'text', text: text.trim() || 'Label', fontSize });
        }
    };

    const labelInput = (
        <div className="flex justify-center pb-1">
            <div className="w-24">
                <TextInput value={label ?? autoLabel} onCommit={v => setLabel(v)} placeholder={autoLabel} />
            </div>
        </div>
    );

    return (
        <section className="border-b border-gray-200 px-4 py-3 dark:border-nightBorder">
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-nightMuted">Capacity</h3>
                <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-nightTextSecondary">{capacity}</span>
            </div>
            <div className="mb-3 grid grid-cols-4 overflow-hidden rounded border border-gray-200 dark:border-nightBorder">
                {TABS.map(({ id, label: tabLabel, Icon }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        className={cx(
                            'flex flex-col items-center gap-0.5 py-1.5 text-[10px] transition-colors',
                            tab === id
                                ? 'bg-primary99 text-primary30 dark:bg-nightAccent/15 dark:text-nightAccent'
                                : 'text-gray-600 hover:bg-gray-50 dark:text-nightTextSecondary dark:hover:bg-nightElevated',
                        )}
                    >
                        <Icon className="h-4 w-4" />
                        {tabLabel}
                    </button>
                ))}
            </div>

            <div className="space-y-2">
                {tab === 'section' && (
                    <>
                        {labelInput}
                        <SelectInput
                            value={sectionType}
                            options={[{ value: 'assigned', label: 'Assigned Seating' }, { value: 'ga', label: 'General Admission' }]}
                            onChange={setSectionType}
                        />
                        {sectionType === 'assigned' ? (
                            <>
                                <Field label="Rows"><NumberInput value={rows} min={1} max={200} onCommit={v => setRows(Math.round(v))} /></Field>
                                <Field label="Seats"><NumberInput value={seats} min={1} max={300} onCommit={v => setSeats(Math.round(v))} /></Field>
                                <p className="text-[11px] text-gray-400">{rows * seats} seats</p>
                            </>
                        ) : (
                            <>
                                <Field label="Type">
                                    <Choice active={gaType === 'standing'} title="Standing" onClick={() => setGaType('standing')}><Footprints className="h-4 w-4" /></Choice>
                                    <Choice active={gaType === 'seated'} title="Seated" onClick={() => setGaType('seated')}><Armchair className="h-4 w-4" /></Choice>
                                </Field>
                                <Toggle label="Show icon" checked={gaIcon} onChange={setGaIcon} />
                                <Field label="Capacity"><NumberInput value={gaCapacity} min={1} max={100000} onCommit={v => setGaCapacity(Math.round(v))} /></Field>
                            </>
                        )}
                    </>
                )}

                {tab === 'table' && (
                    <>
                        {labelInput}
                        <Field label="Style">
                            <Choice active={tableShape === 'round'} title="Round" onClick={() => setTableShape('round')}><Circle className="h-4 w-4" /></Choice>
                            <Choice active={tableShape === 'rect'} title="Square" onClick={() => setTableShape('rect')}><Square className="h-4 w-4" /></Choice>
                            <Choice active={tableShape === 'triangle'} title="Triangle" onClick={() => setTableShape('triangle')}><Triangle className="h-4 w-4" /></Choice>
                        </Field>
                        <Field label="Seats"><NumberInput value={tableSeats} min={1} max={60} onCommit={v => setTableSeats(Math.round(v))} /></Field>
                        {tableShape === 'rect' && (
                            <Field label="End seats" hint="Seats at the two short ends of the table, included in the seat count">
                                <NumberInput value={endSeats} min={0} max={tableSeats} onCommit={v => setEndSeats(Math.round(v))} />
                            </Field>
                        )}
                    </>
                )}

                {tab === 'object' && (
                    <>
                        <Field label="Shape">
                            <Choice active={objectShape === 'square'} title="Rectangle" onClick={() => setObjectShape('square')}><Square className="h-4 w-4" /></Choice>
                            <Choice active={objectShape === 'circle'} title="Circle" onClick={() => setObjectShape('circle')}><Circle className="h-4 w-4" /></Choice>
                            <Choice active={objectShape === 'polygon'} title="Polygon" onClick={() => setObjectShape('polygon')}><Hexagon className="h-4 w-4" /></Choice>
                            <Choice active={objectShape === 'line'} title="Line" onClick={() => setObjectShape('line')}><Minus className="h-4 w-4 -rotate-45" /></Choice>
                        </Field>
                        {objectShape === 'polygon' && (
                            <Field label="Sides"><NumberInput value={sides} min={3} max={12} onCommit={v => setSides(Math.round(v))} /></Field>
                        )}
                        {objectShape !== 'line' && (
                            <>
                                <Field label="Label"><TextInput value={objectName} onCommit={v => setObjectLabel(v)} /></Field>
                                <Toggle label="Object icon" checked={showIcon} onChange={setShowIcon} />
                                {showIcon && (
                                    <IconPicker
                                        value={objectIcon}
                                        onChange={id => {
                                            setObjectIcon(id);
                                            setObjectLabel(null);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}

                {tab === 'text' && (
                    <>
                        <Field label="Text"><TextInput value={text} onCommit={setText} /></Field>
                        <Field label="Size"><NumberInput value={fontSize} min={8} max={96} suffix="px" onCommit={v => setFontSize(Math.round(v))} /></Field>
                    </>
                )}

                <div className="flex justify-end pt-1">
                    <Button variant="primary" onClick={create} className="h-8 px-5">Create</Button>
                </div>
            </div>
        </section>
    );
};

export default AddPanel;
